import { createAsyncThunk } from "@reduxjs/toolkit";
import i18next from "i18next";
import { toast } from "react-toastify";
import { apiCall } from "services/api";

import {
  MeetingMinutesStatuses,
  MeetingResultsSubsectionRequest,
} from "store/meetings/types";
import { Participants } from "store/participants/reducer";

import { toFormData } from "utils/files";
import { trackMeetingCreated } from "utils/gtm";
import { getDownloadMeetingSignedSummaryUrl } from "utils/meeting";

import {
  AgendaFormData,
  AgendaSectionRequest,
  AgendaSubsectionRequest,
  MeetingApprovalFormData,
  MeetingFormData,
  MeetingMinutesRequest,
  MeetingPresenceFormData,
  MeetingSubsectionTextRequest,
  MeetingsFilters,
} from "./types";

export const createMeeting = createAsyncThunk(
  "/meetings/create",
  async (values: MeetingFormData, { rejectWithValue }) => {
    try {
      const response = await apiCall().post(`/meetings`, {
        ...values,
      });
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "createMeetingForm.meetingCreated"
        )
      );

      // Track meeting creation
      if (response.data?.data?.id) {
        trackMeetingCreated(
          response.data.data.id.toString(),
          values.type || "regular"
        );
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteMeeting = createAsyncThunk(
  "/meetings/delete/:meetingId",
  async (meetingId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall().delete(`/meetings/${meetingId}`);
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "createMeetingForm.meetingDeleted"
        )
      );
      return { response: response.data, meetingId: meetingId };
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const putMeeting = createAsyncThunk(
  "/meetings/update/:meetingId",
  async (values: MeetingFormData, { rejectWithValue }) => {
    try {
      const response = await apiCall().put(`/meetings/${values.id}`, values);
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "createMeetingForm.meetingUpdated"
        )
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const putMeetingSummary = createAsyncThunk(
  "/meetings/updateMeetingSummary/:meetingId",
  async (values: any, { rejectWithValue }) => {
    try {
      const response = await apiCall().post(
        `/meetings/${values.id}/updateMeetingSummary`,
        values
      );
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "createMeetingForm.meetingUpdated"
        )
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);
export const getUserAllMeetings = createAsyncThunk(
  "/meetings/user/all",
  async (filters: MeetingsFilters, { rejectWithValue }) => {
    try {
      const response = await apiCall().get("/my/meetings", { params: filters });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getClosestMeetings = createAsyncThunk(
  "/meetings/user/closest",
  async (_, { rejectWithValue }) => {
    try {
      const params = {
        "filter[start_date_after]": new Date().toISOString(),
      };
      const response = await apiCall().get("/my/meetings", { params });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getAllMeetings = createAsyncThunk(
  "/meetings/all",
  async (filters: MeetingsFilters, { rejectWithValue }) => {
    try {
      const response = await apiCall().get("/meetings", { params: filters });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getQuorumTypes = createAsyncThunk(
  "/meetings/quorum-types",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall().get(
        "/enums/single?enum= Domain\\Quorum\\Enums\\MeetingQuorumType"
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getMeetingFrequencies = createAsyncThunk(
  "/meetings/frequency",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall({}).get(
        "/enums/single?enum=Domain\\Meeting\\Enums\\MeetingFrequencies"
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getMeetingRepeatDays = createAsyncThunk(
  "/meetings/repeat-days",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall({}).get(
        "/enums/single?enum=Domain\\Meeting\\Enums\\Days"
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getMeetingTypes = createAsyncThunk(
  "/meetings/types",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall({}).get(
        "/enums/single?enum=Domain\\Meeting\\Enums\\MeetingTypes"
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const addMeetingAgenda = createAsyncThunk(
  "/meetings/agenda/create",
  async (
    { meetingId, agenda }: { meetingId: string; agenda: AgendaFormData },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(`/meetings/${meetingId}/agenda`, {
        ...agenda,
      });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const putMeetingAgenda = createAsyncThunk(
  "/meetings/agenda/update/:meetingId",
  async (
    { meetingId, agenda }: { meetingId: string; agenda: AgendaFormData },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().put(`/meetings/${meetingId}/agenda`, {
        ...agenda,
      });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getMeeting = createAsyncThunk(
  "/meetings/get/:id",
  async (
    {
      meetingId,
      params,
    }: {
      meetingId: number | string;
      params?: {
        seen: Date;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().get(`/meetings/${meetingId}`, {
        params,
      });

      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const postAgendaSection = createAsyncThunk(
  "/meetings/post-agenda-section",
  async (
    {
      agendaId,
      postAgenda,
    }: { agendaId: number; postAgenda: AgendaSectionRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/meetings/agenda/${agendaId}/sections`,
        postAgenda
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const putAgendaSection = createAsyncThunk(
  "/meetings/put-agenda-section",
  async (
    { sectionId, values }: { sectionId: number; values: AgendaSectionRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().put(
        `/meetings/agenda/${values.agenda_id}/sections/${sectionId}`,
        values
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteAgendaSection = createAsyncThunk(
  "/meetings/delete-agenda-section/:id",
  async (
    {
      agendaId,
      sectiondId,
    }: { agendaId: string | number; sectiondId: string | number },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().delete(
        `/meetings/agenda/${agendaId}/sections/${sectiondId}`
      );

      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const getSubsectionTypes = createAsyncThunk(
  "/meetings/subsection-enums",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall({}).get(
        "/enums/single?enum=Domain\\Meeting\\Enums\\SubSectionTypes"
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postAgendaSubsection = createAsyncThunk(
  "/meetings/post-agenda-subsection",
  async (
    {
      agendaId,
      postAgendaSubsection,
    }: {
      agendaId: string;
      postAgendaSubsection: AgendaSubsectionRequest;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/meetings/agenda/${agendaId}/sections/subsections`,
        postAgendaSubsection
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const putAgendaSubsection = createAsyncThunk(
  "/meetings/put-agenda-subsection",
  async (
    {
      agendaId,
      subsectionId,
      values,
    }: {
      agendaId: string | number;
      subsectionId: string | number;
      values: Partial<AgendaSubsectionRequest>;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().put(
        `/meetings/agenda/${agendaId}/sections/subsections/${subsectionId}`,
        values
      );

      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const getAgendaSubsection = createAsyncThunk(
  "/meetings/get-agenda-subsection",
  async (
    {
      agendaId,
      subsectionId,
    }: {
      agendaId: string | number;
      subsectionId: string | number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().get(
        `/meetings/agenda/${agendaId}/sections/subsections/${subsectionId}`
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteAgendaSubsection = createAsyncThunk(
  "/meetings/delete-agenda-subsection/:id",
  async (
    {
      agendaId,
      subsectionId,
    }: {
      agendaId: string | number;
      subsectionId: string | number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().delete(
        `/meetings/agenda/${agendaId}/sections/subsections/${subsectionId}`
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const postMeetingSubsectionText = createAsyncThunk(
  "/meetings/post-subsection-text",
  async (values: MeetingSubsectionTextRequest, { rejectWithValue }) => {
    try {
      const response = await apiCall().post(
        `/meetings/${values.meeting_id}/text-subsections`,
        values
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const patchMeetingSubsectionText = createAsyncThunk(
  "/meetings/get-subsection-text",
  async (values: MeetingSubsectionTextRequest, { rejectWithValue }) => {
    try {
      const response = await apiCall().patch(
        `/meetings/${values.meeting_id}/text-subsections/${values.id}`,
        values
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const postMeetingAttachmentSubsection = createAsyncThunk(
  "/meetings/post-attachment-subsection",
  async (
    {
      meetingId,
      subsectionId,
      file,
    }: {
      meetingId: string | number;
      subsectionId: string | number;
      file: { attachment: File; description: string };
    },
    { rejectWithValue }
  ) => {
    const formData = toFormData({
      attachment: file.attachment,
      description: file.description,
    });
    try {
      const response = await apiCall().post(
        `meetings/${meetingId}/agenda/subsections/${subsectionId}/attachments`,
        formData
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getMeetingAttachmentSubsection = createAsyncThunk(
  "/meetings/get-attachment-subsection",
  async (
    {
      meetingId,
      subsectionId,
      attachmentId,
    }: {
      meetingId: string | number;
      subsectionId: string | number;
      attachmentId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().get(
        `meetings/${meetingId}/agenda/subsections/${subsectionId}/attachments/${attachmentId}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteMeetingAttachmentSubsection = createAsyncThunk(
  "/meetings/delete-attachment-subsection",
  async (
    {
      meetingId,
      subsectionId,
      attachmentId,
    }: {
      meetingId: string | number;
      subsectionId: string | number;
      attachmentId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().delete(
        `meetings/${meetingId}/agenda/subsections/${subsectionId}/attachments/${attachmentId}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postMeetingAttachmentSection = createAsyncThunk(
  "/meetings/post-attachment-section",
  async (
    {
      meetingId,
      sectionId,
      file,
    }: {
      meetingId: string | number;
      sectionId: string | number;
      file: { attachment: File; description: string };
    },
    { rejectWithValue }
  ) => {
    const formData = toFormData({
      attachment: file.attachment,
      description: file.description,
    });
    try {
      const response = await apiCall().post(
        `meetings/${meetingId}/agenda/sections/${sectionId}/attachments`,
        formData
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getMeetingAttachmentSection = createAsyncThunk(
  "/meetings/get-attachment-section",
  async (
    {
      meetingId,
      sectionId,
      attachmentId,
    }: {
      meetingId: string | number;
      sectionId: string | number;
      attachmentId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().get(
        `meetings/${meetingId}/agenda/sections/${sectionId}/attachments/${attachmentId}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteMeetingAttachmentSection = createAsyncThunk(
  "/meetings/delete-attachment-section",
  async (
    {
      meetingId,
      sectionId,
      attachmentId,
    }: {
      meetingId: string | number;
      sectionId: string | number;
      attachmentId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().delete(
        `meetings/${meetingId}/agenda/sections/${sectionId}/attachments/${attachmentId}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const sendToApprovalMeeting = createAsyncThunk(
  "/meetings/send-to-approval",
  async (
    values: { meetingId: string; reviewers: number[] },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/meetings/${values.meetingId}/send-to-approval`,
        { reviewers: values.reviewers }
      );
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "meetingMessages.sendToApproval"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const startReviewMeeting = createAsyncThunk(
  "/meetings/start-review",
  async (meetingId: string, { rejectWithValue }) => {
    try {
      const response = await apiCall().post(
        `/meetings/${meetingId}/start-review`
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const approveMeeting = createAsyncThunk(
  "/meetings/approve",
  async (
    {
      meetingId,
      approveForm,
    }: { meetingId: number; approveForm: MeetingApprovalFormData },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/meetings/${meetingId}/approve`,
        approveForm
      );

      const isApprove = approveForm.status === "accepted";
      const message = `meetingMessages.${isApprove ? "approve" : "disapprove"}`;

      toast.success(
        i18next.getResource(i18next.language, "translation", message)
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const getMeetingAccess = createAsyncThunk(
  "/meetings/access",
  async (meetingId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall({}).get(`/meetings/${meetingId}/room`);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const startMeeting = createAsyncThunk(
  "/meetings/begin",
  async (meetingId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall({}).post(`/meetings/${meetingId}/begin`);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const endMeeting = createAsyncThunk(
  "/meetings/end",
  async (
    {
      meetingId,
      data,
    }: {
      meetingId: number;
      data?: Participants[];
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall({}).post(
        `/meetings/${meetingId}/end`,
        data
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);
export const archiveMeeting = createAsyncThunk(
  "/meetings/archive",
  async (
    {
      meetingId,
      data,
    }: {
      meetingId: number;
      data?: {};
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall({}).patch(
        `/meetings/${meetingId}/archive`,
        {}
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const presenceMeeting = createAsyncThunk(
  "/meetings/presence",
  async (
    {
      meetingId,
      presenceForm,
    }: { meetingId?: number; presenceForm: MeetingPresenceFormData },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/meetings/${meetingId}/presence`,
        presenceForm
      );

      const isAccepted = presenceForm.status === "attending";
      const message = `meetingMessages.${
        isAccepted ? "acceptPresence" : "rejectPresence"
      }`;

      toast.success(
        i18next.getResource(i18next.language, "translation", message)
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const agendaNextSection = createAsyncThunk(
  "/meetings/next-section",
  async ({ meetingId }: { meetingId?: number }, { rejectWithValue }) => {
    try {
      const response = await apiCall().patch(
        `/meetings/${meetingId}/next-section`
      );

      const message = "meetingMessages.successNextSection";

      toast.success(
        i18next.getResource(i18next.language, "translation", message)
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const agendaPreviousSection = createAsyncThunk(
  "/meetings/previous-section",
  async ({ meetingId }: { meetingId?: number }, { rejectWithValue }) => {
    try {
      const response = await apiCall().patch(
        `/meetings/${meetingId}/previous-section`
      );

      const message = "meetingMessages.successPreviousSection";

      toast.success(
        i18next.getResource(i18next.language, "translation", message)
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const createSectionMeetingMinutes = createAsyncThunk(
  "/meeting-minutes/section",
  async (
    {
      meetingId,
      sectionId,
      data,
    }: { meetingId: number; sectionId: number; data: MeetingMinutesRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/meetings/${meetingId}/meeting-minutes/sections/${sectionId}`,
        data
      );

      const message = "meetingMessages.successSectionMeetingMinutes";

      toast.success(
        i18next.getResource(i18next.language, "translation", message)
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateSectionMeetingMinutes = createAsyncThunk(
  "/meeting-minutes/section/:noteId",
  async (
    {
      meetingId,
      sectionId,
      data,
      noteId,
    }: {
      meetingId: number;
      sectionId: number;
      data: MeetingMinutesRequest;
      noteId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().put(
        `/meetings/${meetingId}/meeting-minutes/sections/${sectionId}/${noteId}`,
        data
      );

      const message = "meetingMessages.successSectionMeetingMinutes";

      toast.success(
        i18next.getResource(i18next.language, "translation", message)
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const createSubsectionMeetingMinutes = createAsyncThunk(
  "/meeting-minutes/subsection",
  async (
    {
      meetingId,
      subsectionId,
      data,
    }: { meetingId: number; subsectionId: number; data: MeetingMinutesRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/meetings/${meetingId}/meeting-minutes/subsections/${subsectionId}`,
        data
      );

      const message = "meetingMessages.successSectionMeetingMinutes";

      toast.success(
        i18next.getResource(i18next.language, "translation", message)
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateSubsectionMeetingMinutes = createAsyncThunk(
  "/meeting-minutes/subsection/:noteId",
  async (
    {
      meetingId,
      subsectionId,
      data,
      noteId,
    }: {
      meetingId: number;
      subsectionId: number;
      data: MeetingMinutesRequest;
      noteId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().put(
        `/meetings/${meetingId}/meeting-minutes/subsections/${subsectionId}/${noteId}`,
        data
      );

      const message = "meetingMessages.successSectionMeetingMinutes";

      toast.success(
        i18next.getResource(i18next.language, "translation", message)
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const reviewMeetingMinutes = createAsyncThunk(
  "/meeting-minutes/approve",
  async (
    values: {
      meetingId: number;
      reason?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().patch(
        `/meetings/${values.meetingId}/meeting-minutes/approve`,
        values.reason
          ? {
              reject_reason: values.reason,
              status: MeetingMinutesStatuses.REJECTED,
            }
          : {
              status: MeetingMinutesStatuses.APPROVED,
            }
      );

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          values.reason
            ? "meetingSummary.minutesDisapprovedAction"
            : "meetingSummary.minutesApprovedAction"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const requestMeetingMinutesApprove = createAsyncThunk(
  "/meeting-minutes/request-approve",
  async (
    values: {
      meetingId: number;
      reviewers: number[];
      approval_deadline: string | null;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/meetings/${values.meetingId}/meeting-minutes/request-approval`,
        {
          reviewers: values.reviewers,
          approval_deadline: values.approval_deadline,
        }
      );

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "meetingSummary.minutesRequestedAction"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const sendMeetingMinutesToSign = createAsyncThunk(
  "/meeting-minutes/send-to-sign",
  async (
    values: {
      meetingId: number;
      users: number[];
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/meetings/${values.meetingId}/summary/create`,
        {
          users: values.users,
        }
      );

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "meetingSummary.minutesSentToSignAction"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const uploadSignedMeetingMinutes = createAsyncThunk(
  "/meeting-minutes/sign",
  async (
    values: {
      meetingId: number;
      file: File;
    },
    { rejectWithValue }
  ) => {
    const formData = toFormData({ file: values.file });
    try {
      const response = await apiCall().post(
        `/meetings/${values.meetingId}/summary/upload-signed-summary`,
        formData
      );

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "meetingSummary.minutesSignedAction"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const downloadMeetingSummaryPdf = createAsyncThunk(
  "/meeting-minutes/download-pdf",
  async (meetingId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall().get(
        `/meetings/${meetingId}/summary/download`
      );

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "meetingSummary.minutesDownloadedAction"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const downloadMeetingSignedSummaryPdf = createAsyncThunk(
  "/meeting-minutes/download-signed-pdf",
  async (
    {
      meetingId,
      summaryId,
    }: {
      meetingId: number;
      summaryId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().get(
        getDownloadMeetingSignedSummaryUrl(meetingId, summaryId)
      );

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "meetingSummary.minutesDownloadedAction"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateNote = createAsyncThunk(
  "/meeting-minutes/notes/add-comment",
  async (
    values: {
      meetingId: number;
      noteId: number;
      sectionId?: number;
      subsectionId?: number;
      value: string;
      valueHTML: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().patch(
        `/meetings/${values.meetingId}/meeting-minutes/notes/${values.noteId}/add-comment`,
        {
          contents: values.value,
          contents_html: values.valueHTML,
          type: "note",
        }
      );

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "meetingSummary.noteEditedAction"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const meetingAddAllMembers = createAsyncThunk(
  "/meetings/add-users-from-workspace",
  async (
    { meetingId, workspaceId }: { meetingId: number; workspaceId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/meetings/${meetingId}/add-users-from-workspace/${workspaceId}`
      );

      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const postponeMeeting = createAsyncThunk(
  "/meetings/postpone",
  async (
    {
      meetingId,
      date,
      meeting_address,
    }: { meetingId: number; date: string; meeting_address?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(`/meetings/${meetingId}/postpone`, {
        start_date: date,
        meeting_address: meeting_address || "",
      });

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "meetingMessages.meetingPostponedAction"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateMeetingMinutesConclusion = createAsyncThunk(
  "/meeting-minutes/update-conclusion",
  async (
    { meetingId, conclusion }: { meetingId: number; conclusion: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().put(
        `/meetings/${meetingId}/meeting-minutes`,
        { conclusion }
      );

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "meetingSummary.conclusionUpdated"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const postMeetingSubsectionResults = createAsyncThunk(
  "/meetings/post-subsection-results",
  async (values: MeetingResultsSubsectionRequest, { rejectWithValue }) => {
    try {
      const response = await apiCall().post(
        `/meetings/${values.meeting_id}/result-subsections`,
        values
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const patchMeetingSubsectionResults = createAsyncThunk(
  "/meetings/get-subsection-results",
  async (values: MeetingResultsSubsectionRequest, { rejectWithValue }) => {
    try {
      const response = await apiCall().patch(
        `/meetings/${values.meeting_id}/result-subsections/${values.id}`,
        values
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const reinviteToMeeting = createAsyncThunk(
  "/meetings/invite",
  async (
    { meetingId, userId }: { meetingId: number; userId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/meetings/${meetingId}/invite/${userId}`
      );

      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "meetingAction.resendInvitationSuccess"
        )
      );

      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const patchMeetingMinutesNoteLock = createAsyncThunk(
  "/meetings/meeting-minutes/notes/lock",
  async (
    values: { meetingId: string; noteId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().patch(
        `/meetings/${values.meetingId}/meeting-minutes/notes/${values.noteId}/lock`
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

// TODO: add contents and contents_html like in updateNote method.
export const patchMeetingMinutesNoteUnlock = createAsyncThunk(
  "/meetings/meeting-minutes/notes/add-comment",
  async (
    values: { meetingId: string; noteId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().patch(
        `/meetings/${values.meetingId}/meeting-minutes/notes/${values.noteId}/add-comment`
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const uploadSummaryPdf = createAsyncThunk(
  "/meetings/upload-summary-pdf",
  async (
    { meetingId, file }: { meetingId: number; file: Blob },
    { rejectWithValue }
  ) => {
    const formData = toFormData({
      file: file,
      forced: true,
    });
    try {
      const response = await apiCall().post(
        `/meetings/${meetingId}/summary/upload`,
        formData
      );

      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);
